import type { UiViewDefinition} from '@tyndo/types';

type ViewComponent = React.ComponentType<Record<string, unknown>>;
type ViewLoader = () => Promise<{ default: ViewComponent}>;

interface ViewEntry {
 definition: UiViewDefinition;
 loader: ViewLoader;
 component?: ViewComponent; // cached after first load
}

class ViewRegistry {
 private views = new Map<string, ViewEntry>();
 // Monotonically increasing token bumped every time the registry is mutated.
 // Consumers (ModuleView, useEffect deps) read this so a "View not found"
 // resolve that happened during Pass 1 (gateway-stub) is retried after Pass 2
 // (federated module's real view list) replaces / adds entries.
 private revision = 0;
 private listeners = new Set<() => void>();

 private bump(): void {
 this.revision += 1;
 // Snapshot listeners so a listener that mutates the set during dispatch
 // doesn't break iteration.
 const subs = Array.from(this.listeners);
 for (const fn of subs) {
 try { fn();} catch (err) { console.error('ViewRegistry listener threw', err);}
}
}

 getRevision(): number {
 return this.revision;
}

 /** Subscribe to registry changes. Returns an unsubscribe function. */
 subscribe(fn: () => void): () => void {
 this.listeners.add(fn);
 return () => this.listeners.delete(fn);
}

 register(definition: UiViewDefinition, loader: ViewLoader): void {
 // Compose the storage key from the owning module so two modules can
 // register the same viewId without one overwriting the other. Falls back
 // to `model` for descriptors that predate the moduleId field.
 const scope = definition.moduleId ?? definition.model;
 const key = `${scope}:${definition.viewType}:${definition.viewId}`;
 this.views.set(key, {
 definition,
 loader,
});
 this.bump();
}

 async resolve(
 model: string,
 viewType: string,
 moduleId?: string,
 ): Promise<ViewComponent | null> {
 // Scope by moduleId when supplied so two modules can register the same
 // viewId without colliding. The caller passes the owning module so a
 // viewId like `dashboard` resolves to the right module's component.
 const inModule = (entry: ViewEntry): boolean =>
 !moduleId || entry.definition.moduleId === moduleId;

 const load = async (entry: ViewEntry): Promise<ViewComponent> => {
 if (!entry.component) {
 const loaded = await entry.loader();
 const candidate = loaded?.default;
 // Validate the lazily-loaded export is actually a renderable React
 // component before caching/returning it. A module can register a
 // descriptor whose loader resolves to a non-component (a string, a
 // plain object, null) when its componentName points at the wrong
 // export. Returning that to React produces a cryptic "Element type
 // is invalid" deep in reconciliation with no breadcrumb back to the
 // offending view. Throwing a clear, attributable error here lets
 // ModuleView surface a retry / coming-soon state instead.
 const isComponent =
 typeof candidate === 'function' ||
 // forwardRef / memo / lazy wrappers are objects carrying a $$typeof tag.
 (candidate !== null &&
 typeof candidate === 'object' &&
 '$$typeof' in (candidate as Record<string, unknown>));
 if (!isComponent) {
 const scope = entry.definition.moduleId ?? entry.definition.model;
 const key = `${scope}:${entry.definition.viewType}:${entry.definition.viewId}`;
 throw new Error(
 `View '${key}' loaded a non-component export (got ${candidate === null ? 'null' : typeof candidate}). Check the module's exported componentName.`,
 );
}
 entry.component = candidate as ViewComponent;
}
 return entry.component;
};

 // PRIORITY 0 — exact, fully-scoped match on moduleId + viewType + viewId.
 // When the caller supplies a moduleId we honour it as a hard constraint so
 // two modules that both register a generic viewId (e.g. `dashboard`) can
 // never cross-resolve. This runs before the looser passes below so the
 // most specific descriptor always wins.
 if (moduleId) {
 for (const [, entry] of this.views.entries()) {
 if (
 entry.definition.moduleId === moduleId &&
 (entry.definition as { viewId?: string }).viewId === model &&
 entry.definition.viewType === viewType
 ) {
 return load(entry);
}
}
}

 // PASS 1 — resolve by viewId (model param may actually be a viewId),
 // scoped to the owning module.
 for (const [, entry] of this.views.entries()) {
 if (inModule(entry) && (entry.definition as { viewId?: string }).viewId === model) {
 return load(entry);
}
}
 // PASS 2 — find by model and viewType within the same module scope.
 for (const [, entry] of this.views.entries()) {
 if (
 inModule(entry) &&
 entry.definition.model === model &&
 entry.definition.viewType === viewType
 ) {
 return load(entry);
}
}
 return null;
}

 getDefaultView(model: string): UiViewDefinition | null {
 for (const [, entry] of this.views.entries()) {
 if (entry.definition.model === model && entry.definition.isDefault) {
 return entry.definition;
}
}
 return null;
}

 listViews(model?: string): UiViewDefinition[] {
 const results: UiViewDefinition[] = [];
 for (const [, entry] of this.views.entries()) {
 if (!model || entry.definition.model === model) {
 results.push(entry.definition);
}
}
 return results;
}

 has(model: string, viewType: string): boolean {
 for (const [, entry] of this.views.entries()) {
 if (entry.definition.model === model && entry.definition.viewType === viewType) {
 return true;
}
}
 return false;
}

 clear(): void {
 this.views.clear();
 this.bump();
}
}

export const viewRegistry = new ViewRegistry();
